<script lang="ts">
  import BaseScene from "@engine/BaseScene.svelte";
  import { EVENTS } from "@config/constants/events";

  export let onBack: () => void;

  let masterVolume = 80;
  let musicVolume = 70;
  let sfxVolume = 100;
  let theme = "ocean";
  let fontSize = "medium";
  let highContrast = false;
  let reducedMotion = false;
  let dyslexiaFont = false;
  let colorblind = "off";

  function dispatchSetting(key: string, value: unknown): void {
    window.dispatchEvent(new CustomEvent(EVENTS.SETTINGS_CHANGED, { detail: { key, value } }));
  }
</script>

<BaseScene sceneId="settings" ariaLabel="Settings" onEscape={onBack}>
  <div class="settings">
    <h1>Settings</h1>

    <section aria-labelledby="audio-h">
      <h2 id="audio-h">Audio</h2>
      <label>
        Master Volume
        <input
          type="range"
          min="0"
          max="100"
          bind:value={masterVolume}
          on:input={() => dispatchSetting("masterVolume", masterVolume)}
        />
        <span>{masterVolume}%</span>
      </label>
      <label>
        Music Volume
        <input
          type="range"
          min="0"
          max="100"
          bind:value={musicVolume}
          on:input={() => dispatchSetting("musicVolume", musicVolume)}
        />
        <span>{musicVolume}%</span>
      </label>
      <label>
        Sound Effects
        <input
          type="range"
          min="0"
          max="100"
          bind:value={sfxVolume}
          on:input={() => dispatchSetting("sfxVolume", sfxVolume)}
        />
        <span>{sfxVolume}%</span>
      </label>
    </section>

    <section aria-labelledby="display-h">
      <h2 id="display-h">Display</h2>
      <label>
        Theme
        <select bind:value={theme} on:change={() => dispatchSetting("theme", theme)}>
          <option value="ocean">Ocean (default)</option>
          <option value="high_contrast">High Contrast</option>
          <option value="dyslexia">Dyslexia-friendly</option>
        </select>
      </label>
      <label>
        Text Size
        <select bind:value={fontSize} on:change={() => dispatchSetting("fontSize", fontSize)}>
          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
          <option value="xlarge">Extra Large</option>
        </select>
      </label>
      <label>
        <input
          type="checkbox"
          bind:checked={highContrast}
          on:change={() => dispatchSetting("highContrast", highContrast)}
        />
        High Contrast Mode
      </label>
      <label>
        <input
          type="checkbox"
          bind:checked={reducedMotion}
          on:change={() => dispatchSetting("reducedMotion", reducedMotion)}
        />
        Reduce Motion
      </label>
      <label>
        <input
          type="checkbox"
          bind:checked={dyslexiaFont}
          on:change={() => dispatchSetting("dyslexiaFont", dyslexiaFont)}
        />
        Dyslexia-friendly Font
      </label>
      <label>
        Colorblind Assistance
        <select bind:value={colorblind} on:change={() => dispatchSetting("colorblind", colorblind)}>
          <option value="off">Off</option>
          <option value="protanopia">Protanopia</option>
          <option value="deuteranopia">Deuteranopia</option>
          <option value="tritanopia">Tritanopia</option>
        </select>
      </label>
    </section>

    <section aria-labelledby="about-h">
      <h2 id="about-h">About</h2>
      <p>Version 0.1.0</p>
      <p>A Popphin Learning game</p>
    </section>

    <button type="button" class="settings__back" on:click={onBack}>Back</button>
  </div>
</BaseScene>

<style>
  .settings {
    max-width: 640px;
    margin: 0 auto;
    padding: 2rem 1rem;
  }
  .settings h1 {
    color: var(--color-heading);
    text-align: center;
    margin-bottom: 1.5rem;
  }
  .settings section {
    margin-bottom: 2rem;
  }
  .settings h2 {
    color: var(--color-heading);
    font-size: 1.25rem;
    margin-bottom: 0.75rem;
    border-bottom: 1px solid var(--color-divider);
    padding-bottom: 0.25rem;
  }
  .settings label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    font-family: var(--font-body);
  }
  .settings input[type="range"] {
    flex: 1;
    min-height: 44px;
  }
  .settings input[type="checkbox"] {
    min-width: 24px;
    min-height: 24px;
  }
  .settings select {
    min-height: 44px;
    padding: 0.5em;
  }
  .settings__back {
    min-height: 44px;
    padding: 0.5em 1.5em;
    background: var(--color-surface);
    border: 1px solid var(--color-divider);
    border-radius: 0.5em;
    cursor: pointer;
  }
</style>
